import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type Property = {
  id: string;
  ref: string;
  title: string;
  city: string;
  quartier: string;
  address: string;
  type: string;
  surfaceTotal: number;
  surfaceRDC: number;
  loyer: number;
  status: string;
  completude: number;
  broker: string;
  brokerId: string | null;
  ownerId: string | null;
  createdBy: string | null;
  nbVisites: number;
  bestMatchScore: number;
  bestMatchTenant: string;
  disponibilite: string;
  facade: number;
  hauteurPlafond: number;
  proprietaire: string;
  updatedAt: string;
  hasPhotos: boolean;
  hasDocs: boolean;
  lat: number;
  lng: number;
};

export type Enseigne = {
  id: string;
  nom: string;
  raisonSociale: string;
  secteur: string;
  statut: string;
  villesCibles: string[];
  surfaceMin: number;
  surfaceMax: number;
  budgetMax: number;
  facade: number;
  contact: string;
  nbLocaux: number;
  score: number;
  updatedAt: string;
};

export type Visite = {
  id: string;
  localId: string | null;
  local: string;
  localRef: string;
  enseigne: string;
  contact: string;
  date: string;
  heure: string;
  broker: string;
  brokerId: string | null;
  tenantId: string | null;
  statut: 'Planifiée' | 'Réalisée' | 'Annulée' | 'À reprogrammer';
  note: string;
  priorite: 'Haute' | 'Normale' | 'Basse';
};

export type Document = {
  id: string;
  nom: string;
  type: 'Juridique' | 'Technique' | 'Médias' | 'Administratif' | 'Commercial';
  local: string;
  localRef: string;
  statut: 'Validé' | 'À vérifier' | 'Manquant' | 'Reçu' | 'Expiré';
  uploadedBy: string;
  date: string;
  taille: string;
  version: number;
  permissions: string[];
  visibility: 'private' | 'broker' | 'owner' | 'tenant';
};

export type AppUser = {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  role: 'Administrateur' | 'Broker Senior' | 'Broker' | 'Propriétaire' | 'Locataire' | 'Visiteur';
  nbLocaux: number;
  nbVisites: number;
  derniereConnexion: string;
  dateCreation: string;
  avatar: string;
};

// ─── Row mappers ──────────────────────────────────────────────────────────────

function buildDisplayName(profile: any): string {
  if (!profile) return '';
  const parts = [profile.prenom, profile.nom].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (profile.email || '');
}

function rowToProperty(row: any): Property {
  // Normalize status: handle both 'Loué / retiré' and 'Loué / Retiré' from DB
  const rawStatus = row.status || 'Brouillon';
  const status = rawStatus === 'Loué / retiré' ? 'Loué / Retiré' : rawStatus;

  // Derive broker display name: prefer joined user_profiles, fall back to text field
  const brokerName = row.broker_profile
    ? buildDisplayName(row.broker_profile)
    : (row.broker || '');

  // Derive owner display name: prefer joined user_profiles, fall back to text field
  const ownerName = row.owner_profile
    ? buildDisplayName(row.owner_profile)
    : (row.proprietaire || '');

  return {
    id: row.id,
    ref: row.ref,
    title: row.title,
    city: row.city,
    quartier: row.quartier,
    address: row.address,
    type: row.type,
    surfaceTotal: Number(row.surface_total) || 0,
    surfaceRDC: Number(row.surface_rdc) || 0,
    loyer: Number(row.loyer) || 0,
    status,
    completude: Number(row.completude) || 0,
    broker: brokerName,
    brokerId: row.broker_id || null,
    ownerId: row.owner_id || null,
    createdBy: row.created_by || null,
    nbVisites: Number(row.nb_visites) || 0,
    bestMatchScore: Number(row.best_match_score) || 0,
    bestMatchTenant: row.best_match_tenant || '—',
    disponibilite: row.disponibilite || 'Inconnue',
    facade: Number(row.facade) || 0,
    hauteurPlafond: Number(row.hauteur_plafond) || 0,
    proprietaire: ownerName,
    updatedAt: row.updated_at || '',
    hasPhotos: Boolean(row.has_photos),
    hasDocs: Boolean(row.has_docs),
    lat: Number(row.lat) || 0,
    lng: Number(row.lng) || 0,
  };
}

function rowToEnseigne(row: any): Enseigne {
  return {
    id: row.id,
    nom: row.nom,
    raisonSociale: row.raison_sociale || '',
    secteur: row.secteur || '',
    statut: row.statut,
    villesCibles: row.villes_cibles || [],
    surfaceMin: Number(row.surface_min) || 0,
    surfaceMax: Number(row.surface_max) || 0,
    budgetMax: Number(row.budget_max) || 0,
    facade: Number(row.facade_min) || 0,
    contact: row.contact || '',
    nbLocaux: Number(row.nb_locaux) || 0,
    score: Number(row.score) || 0,
    updatedAt: row.updated_at || '',
  };
}

function rowToVisite(row: any): Visite {
  return {
    id: row.id,
    localId: row.local_id || null,
    local: row.local?.title || '',
    localRef: row.local?.ref || '',
    enseigne: row.tenant ? buildDisplayName(row.tenant) : '',
    contact: row.tenant?.email || '',
    date: row.visite_date || '',
    heure: row.heure || '10:00',
    broker: row.broker_profile ? buildDisplayName(row.broker_profile) : '',
    brokerId: row.broker_id || null,
    tenantId: row.tenant_id || null,
    statut: (row.statut || 'Planifiée') as Visite['statut'],
    note: row.note || '',
    priorite: (row.priorite || 'Normale') as Visite['priorite'],
  };
}

function rowToDocument(row: any): Document {
  return {
    id: row.id,
    nom: row.nom,
    type: row.type as Document['type'],
    local: row.local,
    localRef: row.local_ref,
    statut: row.statut as Document['statut'],
    uploadedBy: row.uploaded_by || '',
    date: row.date || '',
    taille: row.taille || '',
    version: Number(row.version) || 1,
    permissions: row.permissions || [],
    visibility: (row.visibility || 'broker') as Document['visibility'],
  };
}

function rowToUser(row: any): AppUser {
  return {
    id: row.id,
    nom: row.nom || '',
    prenom: row.prenom || '',
    email: row.email || '',
    role: (row.role || 'Visiteur') as AppUser['role'],
    nbLocaux: Number(row.nb_locaux) || 0,
    nbVisites: Number(row.nb_visites) || 0,
    derniereConnexion: row.derniere_connexion || 'Jamais',
    dateCreation: row.date_creation || row.created_at || '',
    avatar: row.avatar || '',
  };
}

// ─── Locaux Service ───────────────────────────────────────────────────────────

export const locauxService = {
  async getAll(): Promise<Property[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('locaux')
      .select(`
        *,
        broker_profile:user_profiles!locaux_broker_id_fkey(id, nom, prenom, email),
        owner_profile:user_profiles!locaux_owner_id_fkey(id, nom, prenom, email)
      `)
      .order('created_at', { ascending: false });
    if (error) { console.error('locauxService.getAll:', error.message); return []; }
    return (data || []).map(rowToProperty);
  },

  async getById(id: string): Promise<Property | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('locaux')
      .select(`
        *,
        broker_profile:user_profiles!locaux_broker_id_fkey(id, nom, prenom, email),
        owner_profile:user_profiles!locaux_owner_id_fkey(id, nom, prenom, email)
      `)
      .eq('id', id)
      .maybeSingle();
    if (error) { console.error('locauxService.getById:', error.message); return null; }
    return data ? rowToProperty(data) : null;
  },

  // Returns the full raw row (all columns) for the detail page
  async getFullById(id: string): Promise<any | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('locaux')
      .select(`
        *,
        broker_profile:user_profiles!locaux_broker_id_fkey(id, nom, prenom, email),
        owner_profile:user_profiles!locaux_owner_id_fkey(id, nom, prenom, email)
      `)
      .eq('id', id)
      .maybeSingle();
    if (error) { console.error('locauxService.getFullById:', error.message); return null; }
    if (!data) return null;
    // Enrich the raw row with resolved display names for convenience
    return {
      ...data,
      broker: data.broker_profile ? buildDisplayName(data.broker_profile) : (data.broker || ''),
      proprietaire: data.owner_profile ? buildDisplayName(data.owner_profile) : (data.proprietaire || ''),
    };
  },

  // Returns all full raw rows (all columns) including ai_extracted_data
  async getAllFull(): Promise<any[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('locaux')
      .select(`
        *,
        broker_profile:user_profiles!locaux_broker_id_fkey(id, nom, prenom, email),
        owner_profile:user_profiles!locaux_owner_id_fkey(id, nom, prenom, email)
      `)
      .order('created_at', { ascending: false });
    if (error) { console.error('locauxService.getAllFull:', error.message); return []; }
    return (data || []).map(row => ({
      ...row,
      broker: row.broker_profile ? buildDisplayName(row.broker_profile) : (row.broker || ''),
      proprietaire: row.owner_profile ? buildDisplayName(row.owner_profile) : (row.proprietaire || ''),
    }));
  },

  // Upload photos to Supabase Storage and return public URLs
  async uploadPhotos(localId: string, files: File[]): Promise<string[]> {
    if (!files.length) return [];
    const supabase = createClient();
    const urls: string[] = [];

    for (const file of files) {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${localId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage
        .from('local-photos')
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) {
        console.error('uploadPhotos error:', error.message);
        continue;
      }
      const { data: urlData } = supabase.storage.from('local-photos').getPublicUrl(path);
      if (urlData?.publicUrl) urls.push(urlData.publicUrl);
    }
    return urls;
  },

  // Get photo URLs for a local from storage
  async getPhotoUrls(localId: string): Promise<string[]> {
    const supabase = createClient();
    const { data, error } = await supabase.storage
      .from('local-photos')
      .list(localId, { sortBy: { column: 'created_at', order: 'asc' } });
    if (error || !data) return [];
    return data.map(file => {
      const { data: urlData } = supabase.storage
        .from('local-photos')
        .getPublicUrl(`${localId}/${file.name}`);
      return urlData?.publicUrl || '';
    }).filter(Boolean);
  },

  async create(property: Omit<Property, 'id'>): Promise<Property | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('locaux')
      .insert({
        ref: property.ref,
        title: property.title,
        city: property.city,
        quartier: property.quartier,
        address: property.address,
        type: property.type,
        surface_total: property.surfaceTotal,
        surface_rdc: property.surfaceRDC,
        loyer: property.loyer,
        status: property.status,
        completude: property.completude,
        // broker (text) intentionally omitted — display-only, derived from broker_id FK join
        broker_id: property.brokerId || null,
        owner_id: property.ownerId || null,
        created_by: property.createdBy || null,
        nb_visites: property.nbVisites,
        best_match_score: property.bestMatchScore,
        best_match_tenant: property.bestMatchTenant,
        disponibilite: property.disponibilite,
        facade: property.facade,
        hauteur_plafond: property.hauteurPlafond,
        // proprietaire (text) intentionally omitted — display-only, derived from owner_id FK join
        updated_at: property.updatedAt,
        has_photos: property.hasPhotos,
        has_docs: property.hasDocs,
        lat: property.lat,
        lng: property.lng,
      })
      .select()
      .single();
    if (error) { console.error('locauxService.create:', error.message); return null; }
    return data ? rowToProperty(data) : null;
  },

  async createFull(form: any, completude: number, publish: boolean): Promise<Property | null> {
    const supabase = createClient();
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

    // Generate a unique ref using timestamp + random to avoid collisions
    const tsFragment = Date.now().toString(36).toUpperCase().slice(-4);
    const randFragment = Math.floor(Math.random() * 9000 + 1000).toString();
    const refNum = `${randFragment}${tsFragment}`;

    // Generate a default title if none provided
    const titleParts = [form.typeLocal || form.usageAutorise || form.type, form.quartier, form.ville].filter(Boolean);
    const defaultTitle = titleParts.length > 0 ? titleParts.join(' — ') : `Local ${randFragment}`;
    const title = (form.titre && form.titre.trim()) ? form.titre.trim() : defaultTitle;

    // Build AI enrichment data for matching
    const aiEnrichment = form.aiExtractedData
      ? JSON.stringify(form.aiExtractedData)
      : null;

    // Ensure FK fields are valid UUIDs or null (never empty string)
    const brokerId = form.brokerId && form.brokerId.trim() ? form.brokerId.trim() : null;
    const ownerId = form.ownerId && form.ownerId.trim() ? form.ownerId.trim() : null;
    const createdBy = form.createdBy && form.createdBy.trim() ? form.createdBy.trim() : null;

    const insertPayload = {
      ref: `LOC-${refNum}`,
      title,
      city: form.ville || '',
      quartier: form.quartier || '',
      address: form.adresse || '',
      type: form.typeLocal || form.type || 'Autre',
      type_local: form.typeLocalCommercial || 'Local commercial',
      offre: form.offre || 'A louer',
      surface_total: Number(form.surfaceTotal) || 0,
      surface_rdc: Number(form.surfaceRDC) || 0,
      surface_mezzanine: Number(form.surfaceMezzanine) || 0,
      surface_sous_sol: Number(form.surfaceSousSol) || 0,
      surface_etage: Number(form.surfaceEtage) || 0,
      surface_terrasse: Number(form.surfaceTerrasse) || 0,
      loyer: Number(form.loyer) || 0,
      droit_entree: Number(form.droitEntree) || 0,
      pas_de_porte: Number(form.pasDePorte) || 0,
      depot_garantie: Number(form.depotGarantie) || 0,
      status: publish ? 'Publié' : (form.statut || 'Brouillon'),
      completude,
      broker_id: brokerId,
      owner_id: ownerId,
      created_by: createdBy,
      nb_visites: 0,
      best_match_score: 0,
      best_match_tenant: '—',
      disponibilite: form.disponibilite || 'Inconnue',
      facade: Number(form.facade) || 0,
      lineaire_vitrine: Number(form.lineaireVitrine) || 0,
      nb_vitrines: Number(form.nbVitrines) || 0,
      hauteur_plafond: Number(form.hauteurPlafond) || 0,
      puissance_electrique: form.puissanceElectrique || '',
      extraction: Boolean(form.extraction),
      climatisation: Boolean(form.climatisation),
      accessibilite: Boolean(form.accessibilite),
      parking: form.parking || '',
      livraison: Boolean(form.livraison),
      etat_local: form.etatLocal || '',
      travaux_necessaires: form.travauxNecessaires || '',
      type_detention: form.typeDetention || '',
      titre_foncier: form.titreFoncier || '',
      usage_autorise: form.usageAutorise || '',
      note_renseignement: form.noteRenseignement || '',
      source: form.source || '',
      priorite: form.priorite || 'Normale',
      zone_commerciale: form.zoneCommerciale || '',
      flux_pieton: form.fluxPieton || '',
      visibilite_enseigne: form.visibiliteEnseigne || '',
      voisinage: form.voisinage || '',
      proximite_transports: form.proximiteTransports || '',
      commentaires_publics: form.commentairesPublics || '',
      commentaires_confidentiels: form.commentairesConfidentiels || '',
      exclusivite: Boolean(form.exclusivite),
      has_photos: false,
      has_docs: false,
      lat: Number(form.lat) || 0,
      lng: Number(form.lng) || 0,
      updated_at: dateStr,
      ai_extracted_data: aiEnrichment,
    };

    let { data, error } = await supabase
      .from('locaux')
      .insert(insertPayload)
      .select()
      .single();

    // If ref collision (unique violation), retry once with a fresh ref
    if (error && (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique'))) {
      const retryTs = Date.now().toString(36).toUpperCase().slice(-5);
      const retryRand = Math.floor(Math.random() * 90000 + 10000).toString();
      const retryPayload = { ...insertPayload, ref: `LOC-${retryRand}${retryTs}` };
      const retryResult = await supabase.from('locaux').insert(retryPayload).select().single();
      data = retryResult.data;
      error = retryResult.error;
    }

    if (error) {
      console.error('locauxService.createFull error:', error.message, '| code:', error.code, '| details:', error.details, '| hint:', error.hint);
      return null;
    }
    return data ? rowToProperty(data) : null;
  },

  async updateFull(id: string, form: any, completude: number, publish: boolean): Promise<Property | null> {
    const supabase = createClient();
    const dateStr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

    const titleParts = [form.typeLocal || form.type, form.quartier, form.ville].filter(Boolean);
    const defaultTitle = titleParts.join(' — ') || 'Local';
    const title = (form.titre && form.titre.trim()) ? form.titre.trim() : defaultTitle;

    // Build AI enrichment data for matching
    const aiEnrichment = form.aiExtractedData
      ? JSON.stringify(form.aiExtractedData)
      : undefined;

    const updatePayload: any = {
      title,
      city: form.ville || '',
      quartier: form.quartier || '',
      address: form.adresse || '',
      type: form.typeLocal || form.type || 'Autre',
      type_local: form.typeLocalCommercial || 'Local commercial',
      offre: form.offre || 'A louer',
      surface_total: Number(form.surfaceTotal) || 0,
      surface_rdc: Number(form.surfaceRDC) || 0,
      surface_mezzanine: Number(form.surfaceMezzanine) || 0,
      surface_sous_sol: Number(form.surfaceSousSol) || 0,
      surface_etage: Number(form.surfaceEtage) || 0,
      surface_terrasse: Number(form.surfaceTerrasse) || 0,
      loyer: Number(form.loyer) || 0,
      droit_entree: Number(form.droitEntree) || 0,
      pas_de_porte: Number(form.pasDePorte) || 0,
      depot_garantie: Number(form.depotGarantie) || 0,
      status: publish ? 'Publié' : (form.statut || 'Brouillon'),
      completude,
      // broker (text) intentionally omitted — display-only, derived from broker_id FK join
      broker_id: form.brokerId !== undefined ? (form.brokerId || null) : undefined,
      owner_id: form.ownerId !== undefined ? (form.ownerId || null) : undefined,
      disponibilite: form.disponibilite || 'Inconnue',
      facade: Number(form.facade) || 0,
      lineaire_vitrine: Number(form.lineaireVitrine) || 0,
      nb_vitrines: Number(form.nbVitrines) || 0,
      hauteur_plafond: Number(form.hauteurPlafond) || 0,
      puissance_electrique: form.puissanceElectrique || '',
      extraction: Boolean(form.extraction),
      climatisation: Boolean(form.climatisation),
      accessibilite: Boolean(form.accessibilite),
      parking: form.parking || '',
      livraison: Boolean(form.livraison),
      etat_local: form.etatLocal || '',
      travaux_necessaires: form.travauxNecessaires || '',
      // proprietaire (text) intentionally omitted — display-only, derived from owner_id FK join
      type_detention: form.typeDetention || '',
      titre_foncier: form.titreFoncier || '',
      usage_autorise: form.usageAutorise || '',
      note_renseignement: form.noteRenseignement || '',
      source: form.source || '',
      priorite: form.priorite || 'Normale',
      zone_commerciale: form.zoneCommerciale || '',
      flux_pieton: form.fluxPieton || '',
      visibilite_enseigne: form.visibiliteEnseigne || '',
      voisinage: form.voisinage || '',
      proximite_transports: form.proximiteTransports || '',
      commentaires_publics: form.commentairesPublics || '',
      commentaires_confidentiels: form.commentairesConfidentiels || '',
      exclusivite: Boolean(form.exclusivite),
      lat: Number(form.lat) || 0,
      lng: Number(form.lng) || 0,
      updated_at: dateStr,
    };

    if (aiEnrichment !== undefined) {
      updatePayload.ai_extracted_data = aiEnrichment;
    }

    const { data, error } = await supabase
      .from('locaux')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('locauxService.updateFull:', error.message); return null; }
    return data ? rowToProperty(data) : null;
  },

  async update(id: string, updates: Partial<Property>): Promise<Property | null> {
    const supabase = createClient();
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.city !== undefined) dbUpdates.city = updates.city;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.loyer !== undefined) dbUpdates.loyer = updates.loyer;
    if (updates.completude !== undefined) dbUpdates.completude = updates.completude;
    if (updates.hasPhotos !== undefined) dbUpdates.has_photos = updates.hasPhotos;
    if (updates.hasDocs !== undefined) dbUpdates.has_docs = updates.hasDocs;
    dbUpdates.updated_at = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

    const { data, error } = await supabase
      .from('locaux')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('locauxService.update:', error.message); return null; }
    return data ? rowToProperty(data) : null;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('locaux').delete().eq('id', id);
    if (error) { console.error('locauxService.delete:', error.message); return false; }
    return true;
  },
};

// ─── Enseignes Service ────────────────────────────────────────────────────────

export const enseignesService = {
  async getAll(): Promise<Enseigne[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('enseignes')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('enseignesService.getAll:', error.message); return []; }
    return (data || []).map(rowToEnseigne);
  },

  async create(form: any): Promise<Enseigne | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('enseignes')
      .insert({
        nom: form.nom,
        raison_sociale: form.raisonSociale || form.nom,
        secteur: form.secteur || 'Autre',
        statut: form.statut || 'Prospect',
        contact: form.contact || '',
        villes_cibles: form.villesCibles || [],
        surface_min: Number(form.surfaceMin) || 0,
        surface_max: Number(form.surfaceMax) || 0,
        surface_rdc_min: Number(form.surfaceRDCMin) || 0,
        facade_min: Number(form.facadeMin) || 0,
        budget_max: Number(form.budgetMax) || 0,
        droit_entree_max: Number(form.droitEntreeMax) || 0,
        travaux_pris_en_charge: Boolean(form.travauxPrisEnCharge),
        extraction: Boolean(form.extraction),
        hauteur_min: Number(form.hauteurMin) || 0,
        puissance_min: form.puissanceMin || '',
        parking: Boolean(form.parking),
        livraison: Boolean(form.livraison),
        accessibilite: Boolean(form.accessibilite),
        terrasse: Boolean(form.terrasse),
        urgence: form.urgence || 'Normale',
        horizon_ouverture: form.horizonOuverture || '',
        nb_ouvertures_prevues: Number(form.nbOuverturesPrevues) || 0,
        centres_commerciaux: form.centresCommerciaux || '',
        quartiers_exclus: form.quartiersExclus || '',
        notes: form.notes || '',
        nb_locaux: 0,
        score: 0,
        updated_at: new Date().toLocaleDateString('fr-FR'),
      })
      .select()
      .single();
    if (error) { console.error('enseignesService.create:', error.message); return null; }
    return data ? rowToEnseigne(data) : null;
  },

  async update(id: string, form: any): Promise<Enseigne | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('enseignes')
      .update({
        nom: form.nom,
        raison_sociale: form.raisonSociale,
        secteur: form.secteur,
        statut: form.statut,
        contact: form.contact,
        villes_cibles: form.villesCibles || [],
        surface_min: Number(form.surfaceMin) || 0,
        surface_max: Number(form.surfaceMax) || 0,
        facade_min: Number(form.facadeMin) || 0,
        budget_max: Number(form.budgetMax) || 0,
        updated_at: new Date().toLocaleDateString('fr-FR'),
      })
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('enseignesService.update:', error.message); return null; }
    return data ? rowToEnseigne(data) : null;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('enseignes').delete().eq('id', id);
    if (error) { console.error('enseignesService.delete:', error.message); return false; }
    return true;
  },
};

// ─── Visites Service ──────────────────────────────────────────────────────────

export const visitesService = {
  async getAll(): Promise<Visite[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('visites')
      .select(`
        *,
        local:locaux(id, ref, title),
        tenant:user_profiles!visites_tenant_id_fkey(id, nom, prenom, email),
        broker_profile:user_profiles!visites_broker_id_fkey(id, nom, prenom, email)
      `)
      .order('created_at', { ascending: false });
    if (error) { console.error('visitesService.getAll:', error.message); return []; }
    return (data || []).map((row: any) => ({
      id: row.id,
      localId: row.local_id || null,
      local: row.local?.title || '',
      localRef: row.local?.ref || '',
      enseigne: row.tenant ? buildDisplayName(row.tenant) : '',
      contact: row.tenant?.email || '',
      date: row.visite_date || '',
      heure: row.heure || '10:00',
      broker: row.broker_profile ? buildDisplayName(row.broker_profile) : '',
      brokerId: row.broker_id || null,
      tenantId: row.tenant_id || null,
      statut: (row.statut || 'Planifiée') as Visite['statut'],
      note: row.note || '',
      priorite: (row.priorite || 'Normale') as Visite['priorite'],
    }));
  },

  async create(form: any): Promise<Visite | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('visites')
      .insert({
        local_id: form.localId || form.local_id || null,
        tenant_id: form.tenantId || form.tenant_id || null,
        broker_id: form.brokerId || form.broker_id || null,
        visite_date: form.date || form.visite_date || null,
        statut: 'Planifiée',
        heure: form.heure || '10:00',
        note: form.note || '',
        priorite: form.priorite || 'Normale',
        // Text display columns intentionally omitted — derived from FK joins at read time
      })
      .select()
      .single();
    if (error) { console.error('visitesService.create:', error.message); return null; }
    return data ? rowToVisite(data) : null;
  },

  async update(id: string, form: any): Promise<Visite | null> {
    const supabase = createClient();
    const updates: any = {};
    // FK relational columns — source of truth
    if (form.localId !== undefined) updates.local_id = form.localId;
    if (form.brokerId !== undefined) updates.broker_id = form.brokerId;
    if (form.tenantId !== undefined) updates.tenant_id = form.tenantId;
    // Non-relational fields
    if (form.date !== undefined) updates.visite_date = form.date;
    if (form.heure !== undefined) updates.heure = form.heure;
    if (form.priorite !== undefined) updates.priorite = form.priorite;
    if (form.note !== undefined) updates.note = form.note;
    if (form.statut !== undefined) updates.statut = form.statut;
    // Text display columns (local, local_ref, enseigne, contact, broker) intentionally omitted
    // — they are display-only fields derived from FK joins at read time

    const { data, error } = await supabase
      .from('visites')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('visitesService.update:', error.message); return null; }
    return data ? rowToVisite(data) : null;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('visites').delete().eq('id', id);
    if (error) { console.error('visitesService.delete:', error.message); return false; }
    return true;
  },
};

// ─── Documents Service ────────────────────────────────────────────────────────

export const documentsService = {
  async getAll(): Promise<Document[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('documentsService.getAll:', error.message); return []; }
    return (data || []).map(rowToDocument);
  },

  async create(form: any, file: File | null): Promise<Document | null> {
    const supabase = createClient();
    const taille = file
      ? file.size > 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(1)} Mo`
        : `${Math.round(file.size / 1024)} Ko`
      : '—';

    const { data, error } = await supabase
      .from('documents')
      .insert({
        nom: file ? file.name : form.nom || 'Document',
        type: form.type || 'Juridique',
        local: form.local || '',
        local_ref: form.localRef || '',
        statut: form.statut || 'À vérifier',
        uploaded_by: 'Vous',
        date: new Date().toLocaleDateString('fr-FR'),
        taille,
        version: 1,
        permissions: form.permissions || ['Admin', 'Broker'],
        visibility: form.visibility || 'broker',
      })
      .select()
      .single();
    if (error) { console.error('documentsService.create:', error.message); return null; }
    return data ? rowToDocument(data) : null;
  },

  async update(id: string, updates: Partial<Document>): Promise<Document | null> {
    const supabase = createClient();
    const dbUpdates: any = {};
    if (updates.statut !== undefined) dbUpdates.statut = updates.statut;
    if (updates.type !== undefined) dbUpdates.type = updates.type;
    if (updates.permissions !== undefined) dbUpdates.permissions = updates.permissions;
    if (updates.visibility !== undefined) dbUpdates.visibility = updates.visibility;
    dbUpdates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('documents')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('documentsService.update:', error.message); return null; }
    return data ? rowToDocument(data) : null;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('documents').delete().eq('id', id);
    if (error) { console.error('documentsService.delete:', error.message); return false; }
    return true;
  },
};

// ─── Users Service ────────────────────────────────────────────────────────────

export const usersService = {
  async getAll(): Promise<AppUser[]> {
    const supabase = createClient();
    // user_profiles is the single source of truth for users & roles
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('usersService.getAll:', error.message); return []; }
    return (data || []).map(rowToUser);
  },

  async create(form: { prenom: string; nom: string; email: string; role: string }): Promise<AppUser | null> {
    const supabase = createClient();
    // Create in user_profiles only (new schema — no team_members)
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        email: form.email,
        nom: form.nom,
        prenom: form.prenom,
        role: form.role,
      })
      .select()
      .single();
    if (error) { console.error('usersService.create:', error.message); return null; }
    if (!data) return null;
    return rowToUser(data);
  },

  async update(id: string, updates: Partial<AppUser>): Promise<AppUser | null> {
    const supabase = createClient();
    const dbUpdates: any = {};
    if (updates.nom !== undefined) dbUpdates.nom = updates.nom;
    if (updates.prenom !== undefined) dbUpdates.prenom = updates.prenom;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.role !== undefined) dbUpdates.role = updates.role;
    if (updates.avatar !== undefined) dbUpdates.avatar = updates.avatar;

    const { data, error } = await supabase
      .from('user_profiles')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) { console.error('usersService.update:', error.message); return null; }
    if (!data) return null;
    return rowToUser(data);
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('user_profiles').delete().eq('id', id);
    if (error) { console.error('usersService.delete:', error.message); return false; }
    return true;
  },
};

// ─── Stats Service ────────────────────────────────────────────────────────────

export type AppStats = {
  totalLocaux: number;
  publies: number;
  enRevue: number;
  brouillons: number;
  enDiscussion: number;
  offreRecue: number;
  enNegociation: number;
  visite: number;
  aFortPotentiel: number;
  enseignesEnRecherche: number;
  totalEnseignes: number;
  totalVisites: number;
  visitesPlanifiees: number;
  visitesRealisees: number;
  visitesAnnulees: number;
  visitesAReprogrammer: number;
  totalDocs: number;
  docsValides: number;
  docsAVerifier: number;
  docsManquants: number;
  docsExpires: number;
  locauxSansDocs: number;
  tauxMatching: number;
  dealsEnNegociation: number;
  avgCompletude: number;
  avgLoyer: number;
  staleLocaux: number;
  byCity: { ville: string; locaux: number; loyer: number }[];
  statutDistribution: { name: string; value: number; color: string }[];
  brokerPerf: { broker: string; locaux: number; visites: number; matchs: number; taux: number }[];
};

export async function fetchStats(): Promise<AppStats> {
  const supabase = createClient();

  const [locauxRes, enseignesRes, visitesRes, docsRes] = await Promise.all([
    supabase.from('locaux').select('*'),
    supabase.from('enseignes').select('statut'),
    supabase.from('visites').select('statut, broker'),
    supabase.from('documents').select('statut'),
  ]);

  const properties = (locauxRes.data || []).map(rowToProperty);
  const enseignes = enseignesRes.data || [];
  const visites = visitesRes.data || [];
  const docs = docsRes.data || [];

  const totalLocaux = properties.length;
  const publies = properties.filter(p => p.status === 'Publié').length;
  const enRevue = properties.filter(p => p.status === 'En revue').length;
  const brouillons = properties.filter(p => p.status === 'Brouillon').length;
  const enDiscussion = properties.filter(p => p.status === 'En discussion').length;
  const offreRecue = properties.filter(p => p.status === 'Offre reçue').length;
  const enNegociation = properties.filter(p => p.status === 'En négociation').length;
  const visite = properties.filter(p => p.status === 'Visité').length;
  const aFortPotentiel = properties.filter(p => p.status === 'À fort potentiel').length;

  const enseignesEnRecherche = enseignes.filter((e: any) => e.statut === 'En recherche').length;
  const totalEnseignes = enseignes.length;

  const totalVisites = visites.length;
  const visitesPlanifiees = visites.filter((v: any) => v.statut === 'Planifiée').length;
  const visitesRealisees = visites.filter((v: any) => v.statut === 'Réalisée').length;
  const visitesAnnulees = visites.filter((v: any) => v.statut === 'Annulée').length;
  const visitesAReprogrammer = visites.filter((v: any) => v.statut === 'À reprogrammer').length;

  const totalDocs = docs.length;
  const docsValides = docs.filter((d: any) => d.statut === 'Validé').length;
  const docsAVerifier = docs.filter((d: any) => d.statut === 'À vérifier').length;
  const docsManquants = docs.filter((d: any) => d.statut === 'Manquant').length;
  const docsExpires = docs.filter((d: any) => d.statut === 'Expiré').length;
  const locauxSansDocs = properties.filter(p => !p.hasDocs).length;

  const dealsEnNegociation = properties.filter(p => p.status === 'En négociation' || p.status === 'En discussion').length;
  const highScoreMatchs = properties.filter(p => p.bestMatchScore >= 70).length;
  const tauxMatching = totalLocaux > 0 ? Math.round((highScoreMatchs / totalLocaux) * 100) : 0;

  const avgCompletude = totalLocaux > 0
    ? Math.round(properties.reduce((s, p) => s + p.completude, 0) / totalLocaux)
    : 0;
  const avgLoyer = totalLocaux > 0
    ? Math.round(properties.reduce((s, p) => s + p.loyer, 0) / totalLocaux / 1000)
    : 0;

  const cities = Array.from(new Set(properties.map(p => p.city)));
  const byCity = cities.map(city => {
    const cityProps = properties.filter(p => p.city === city);
    return {
      ville: city,
      locaux: cityProps.length,
      loyer: cityProps.length > 0 ? Math.round(cityProps.reduce((s, p) => s + p.loyer, 0) / cityProps.length) : 0,
    };
  }).sort((a, b) => b.locaux - a.locaux);

  const statutDistribution = [
    { name: 'Publié', value: publies + aFortPotentiel, color: '#10b981' },
    { name: 'En discussion', value: enDiscussion, color: '#3b82f6' },
    { name: 'Offre reçue', value: offreRecue, color: '#8b5cf6' },
    { name: 'En négociation', value: enNegociation, color: '#f59e0b' },
    { name: 'Visité', value: visite, color: '#06b6d4' },
    { name: 'Brouillon / En revue', value: brouillons + enRevue, color: '#94a3b8' },
  ].filter(s => s.value > 0);

  const brokerNames = ['K. Alami', 'S. Benali', 'N. Tahiri', 'M. Ouali'];
  const brokerPerf = brokerNames.map(broker => {
    const brokerLocaux = properties.filter(p => p.broker === broker).length;
    const brokerVisites = visites.filter((v: any) => v.broker === broker).length;
    const brokerDeals = properties.filter(p =>
      p.broker === broker && (p.status === 'En discussion' || p.status === 'En négociation')
    ).length;
    const taux = brokerVisites > 0 ? Math.round((brokerDeals / brokerVisites) * 100) : 0;
    return { broker, locaux: brokerLocaux, visites: brokerVisites, matchs: brokerDeals, taux };
  });

  // Locaux not updated in the last 30 days (stale)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const staleLocaux = (locauxRes.data || []).filter((row: any) => {
    if (!row.created_at) return false;
    return new Date(row.created_at) < thirtyDaysAgo;
  }).length;

  return {
    totalLocaux, publies, enRevue, brouillons, enDiscussion, offreRecue, enNegociation,
    visite, aFortPotentiel, enseignesEnRecherche, totalEnseignes,
    totalVisites, visitesPlanifiees, visitesRealisees, visitesAnnulees, visitesAReprogrammer,
    totalDocs, docsValides, docsAVerifier, docsManquants, docsExpires, locauxSansDocs,
    tauxMatching, dealsEnNegociation, avgCompletude, avgLoyer, staleLocaux,
    byCity, statutDistribution, brokerPerf,
  };
}
